import { useDebounce } from '@aibees/shared/hooks';
import { classnames, getRangeInputError } from '@aibees/shared/utils';
import { Box, MenuItemProps, Stack } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { FilterInputError } from '../number-range-input/components/filter-input-error';
import { ErrorMessage } from '../number-range-input/config';
import { NumberSingleSelect } from './components/number-single-select';
import * as Styled from './styles';
import * as SX from './sx';
import type { NumberRangeSelectProps as Props } from './types';

export const NumberRangeSelect = memo(
  ({
    className,
    defaultValue,
    options,
    disabled,
    value,
    error,
    metadata,
    onChangeRange,
  }: Props) => {
    const debouncedValue = useDebounce(value, 0);
    const [v1, setV1] = useState(debouncedValue?.at(0));
    const [v2, setV2] = useState(debouncedValue?.at(1));

    useEffect(() => {
      setV1(debouncedValue?.at(0) ?? '');
      if (typeof debouncedValue?.at(1) === 'undefined') setV2('');
      else setV2(debouncedValue?.at(1));
    }, [debouncedValue]);
    const handleChangeStart: MenuItemProps['onChange'] = (e) => {
      const v = e.currentTarget.getAttribute('data-value');
      setV1(v);
      if (v && !getRangeInputError(v, v2)) {
        onChangeRange?.(v, v2);
      }
    };

    const handleChangeEnd: MenuItemProps['onChange'] = (e) => {
      const v = e.currentTarget.getAttribute('data-value');
      setV2(v);

      if (v && !getRangeInputError(v1, v)) {
        onChangeRange?.(v1, v);
      }
    };

    const inputError = useMemo(() => {
      return getRangeInputError(v1, v2);
    }, [v1, v2]);

    return (
      <Box
        className={classnames('NumberRangeSelect', className)}
        display="flex"
        flexDirection="column"
        gap={0.5}
        sx={SX.root}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <NumberSingleSelect
            key="min"
            // label="Min"
            placeholder={metadata?.numberRange.startPlaceholder}
            value={v1}
            error={error}
            defaultValue={defaultValue?.at(0)}
          >
            {options.map((option) => (
              <Styled.MenuItem
                onClick={handleChangeStart}
                key={option.value}
                value={option.value}
              >
                {option.label ?? option.value}
              </Styled.MenuItem>
            ))}
          </NumberSingleSelect>
          -
          <NumberSingleSelect
            key="max"
            // label="Max"
            placeholder={metadata?.numberRange.endPlaceholder}
            value={v2}
            error={error}
            disabled={disabled}
          >
            {options.map((option) => (
              <Styled.MenuItem
                onClick={handleChangeEnd}
                key={option.value}
                value={option.value}
              >
                {option.label ?? option.value}
              </Styled.MenuItem>
            ))}
          </NumberSingleSelect>
        </Stack>
        {!!inputError && (
          <FilterInputError message={ErrorMessage[inputError]} />
        )}
      </Box>
    );
  }
);

NumberRangeSelect.displayName = 'NumberRangeSelect';
